import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Icon from './Icon';

/**
 * ConfirmDialog — 확인 대화상자 컴포넌트
 *
 * 되돌릴 수 없는 작업(삭제 등) 전에 사용자 확인을 받는 모달.
 * 포커스 트랩, ESC 닫기, 오버레이 클릭 닫기를 기본 제공한다.
 *
 * @param {Object} props
 * @param {string} props.title - 대화상자 제목
 * @param {string} props.message - 대화상자 메시지
 * @param {string} [props.confirmText='확인'] - 확인 버튼 텍스트
 * @param {string} [props.cancelText='취소'] - 취소 버튼 텍스트
 * @param {'info'|'warning'|'error'} [props.type='warning'] - 유형
 * @param {boolean} [props.passwordRequired=false] - 비밀번호 입력 필드 표시 여부
 * @param {Function} props.onConfirm - 확인 콜백 (passwordRequired 시 password 문자열 전달)
 * @param {Function} props.onCancel - 취소 콜백
 * @returns {React.ReactElement}
 *
 * 사용 예:
 *   <ConfirmDialog
 *     title="거래 삭제"
 *     message="정말 삭제하시겠습니까?"
 *     type="error"
 *     confirmText="삭제"
 *     onConfirm={() => handleDelete()}
 *     onCancel={() => setOpen(false)}
 *   />
 */

const DIALOG_CONFIG = {
  info: { icon: 'info', colorVar: '--color-info' },
  warning: { icon: 'warning', colorVar: '--color-warning' },
  error: { icon: 'error', colorVar: '--color-error' },
};

const ConfirmDialog = ({
  title,
  message,
  confirmText = '확인',
  cancelText = '취소',
  type = 'warning',
  passwordRequired = false,
  onConfirm,
  onCancel,
}) => {
  const [password, setPassword] = useState('');
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);

  const config = DIALOG_CONFIG[type] || DIALOG_CONFIG.warning;

  // 이전 포커스 저장 + 초기 포커스 설정
  useEffect(() => {
    previousFocusRef.current = document.activeElement;

    // 첫 번째 포커스 가능 요소에 포커스
    const dialog = dialogRef.current;
    if (dialog) {
      const focusable = dialog.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length > 0) {
        focusable[0].focus();
      }
    }

    return () => {
      // 이전 포커스 복원
      if (previousFocusRef.current && previousFocusRef.current.focus) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  // ESC 키 닫기
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  // 포커스 트랩 (Tab/Shift+Tab 순환)
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      const focusable = dialog.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: 첫 번째에서 마지막으로 순환
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab: 마지막에서 첫 번째로 순환
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, []);

  // 오버레이 클릭 닫기 (dialog body 클릭은 전파 중지)
  const handleOverlayClick = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleDialogClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // 확인 핸들러
  const handleConfirm = useCallback(() => {
    if (passwordRequired) {
      onConfirm(password);
    } else {
      onConfirm();
    }
  }, [passwordRequired, password, onConfirm]);

  const isConfirmDisabled = passwordRequired && password.trim() === '';

  return (
    <div className="dialog-overlay" onClick={handleOverlayClick}>
      <div
        className="dialog"
        ref={dialogRef}
        onClick={handleDialogClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
      >
        {/* 헤더: 아이콘 + 제목 */}
        <div className="dialog__header">
          <Icon
            name={config.icon}
            size={24}
            color={`var(${config.colorVar})`}
            className="dialog__icon"
          />
          <h3 id="dialog-title" className="dialog__title">{title}</h3>
        </div>

        {/* 메시지 */}
        <p className="dialog__message">{message}</p>

        {/* 비밀번호 입력 필드 (passwordRequired=true 시) */}
        {passwordRequired && (
          <div className="dialog__password-field">
            <input
              type="password"
              className="form-input"
              placeholder="비밀번호를 입력하세요"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
        )}

        {/* 버튼 영역 */}
        <div className="dialog__actions">
          <button
            type="button"
            className="btn-secondary dialog__cancel-btn"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`dialog__confirm-btn ${type === 'error' ? 'btn-danger' : 'btn-primary'}`}
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

ConfirmDialog.propTypes = {
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  type: PropTypes.oneOf(['info', 'warning', 'error']),
  passwordRequired: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export { DIALOG_CONFIG };
export default ConfirmDialog;
